import dayjs from 'dayjs'
import qs from 'qs'
import { useSearchParams } from 'react-router-dom'
import type { RangeValue } from 'types/shared'

const QUERY_ARRAY_LIMIT = 100
export type AllFiltersGeneric<StateType> = StateType & {
  rangePicker?: RangeValue<dayjs.Dayjs>
}

const useFilterParamsGeneric = <StateType = Record<string, unknown>>(
  initialFiltersValue: AllFiltersGeneric<StateType>
) => {
  const initialString = transformAllFiltersToString(initialFiltersValue)

  const [params, setParams] = useSearchParams(initialString)

  const filterParamsGeneric = transformStringToAllFilters<StateType>(
    params.toString()
  )

  const setFilterParams = (
    getState: (
      prevState: AllFiltersGeneric<StateType>
    ) => AllFiltersGeneric<StateType>
  ) => {
    const newState = getState(filterParamsGeneric)
    const newQueryParams = transformAllFiltersToString(newState)

    setParams(newQueryParams)
  }

  const queryParams = transformAllFiltersToString(initialFiltersValue)

  return { filterParamsGeneric, setFilterParams, queryParams } as const
}

const transformAllFiltersToString = <StateType>(
  params: AllFiltersGeneric<StateType>
): string => {
  const rangePicker = params?.rangePicker?.map((date) =>
    date?.toISOString?.()
  ) as AllFiltersGeneric<StateType>['rangePicker']

  const newQueryString = qs.stringify({
    ...params,
    rangePicker
  })

  return newQueryString
}

export const transformStringToAllFilters = <StateType>(
  queryParams: string
): AllFiltersGeneric<StateType> => {
  const newFilterParams =
    (qs.parse(queryParams, { arrayLimit: QUERY_ARRAY_LIMIT }) as Omit<
      AllFiltersGeneric<StateType>,
      'rangePicker'
    > & {
      rangePicker: [string | null, string | null] | undefined | null
    }) || {}

  if (newFilterParams?.rangePicker) {
    ;(newFilterParams.rangePicker as AllFiltersGeneric<StateType>['rangePicker']) =
      newFilterParams.rangePicker?.map((dateString) =>
        dayjs(dateString)
      ) as AllFiltersGeneric<StateType>['rangePicker']
  }

  return newFilterParams as AllFiltersGeneric<StateType>
}

export default useFilterParamsGeneric
